import React, { useCallback, useEffect, useRef, useState } from "react";
import FeatherIcon from "feather-icons-react";
import { previewNotice } from "../noticesApi";

const TABS = [
  { key: "email", label: "Email" },
  { key: "sms", label: "Text" },
  { key: "page", label: "Resident page" },
];

/**
 * The notice as a resident will actually receive it.
 *
 * The email tab shows HTML rendered by the server from the real template, so what is on
 * screen is what goes out. It is deliberately not a React rebuild of the email: a second
 * implementation drifts from the template and then lies.
 */
const NoticePreview = ({ noticeId, draft, onClose }) => {
  const [tab, setTab] = useState("email");
  const [wide, setWide] = useState(true);
  const [personId, setPersonId] = useState(null);
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const timer = useRef(null);

  const load = useCallback(() => {
    if (!noticeId) return;
    setState((s) => ({ ...s, loading: true }));
    previewNotice(noticeId, {
      title: draft.title,
      message: draft.message,
      type: draft.type,
      recipient_id: personId,
    })
      .then((res) => {
        if (!res?.success) throw new Error(res?.message || "The preview could not be built.");
        setState({ loading: false, error: "", data: res.data });
      })
      .catch((err) => {
        setState({ loading: false, error: err?.response?.data?.message || err.message || "The preview could not be built.", data: null });
      });
  }, [noticeId, draft.title, draft.message, draft.type, personId]);

  // Debounced, so typing a title does not fire a render per keystroke.
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(load, 400);
    return () => clearTimeout(timer.current);
  }, [load]);

  const d = state.data;
  const people = d?.people || [];

  return (
    <div className="rn-preview">
      <div className="rn-preview-bar">
        <div className="rn-seg" role="tablist">
          {TABS.map((t) => (
            <button key={t.key} type="button" className={tab === t.key ? "on" : ""} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "email" && (
          <button type="button" className="rn-ib" title={wide ? "Phone width" : "Desktop width"} onClick={() => setWide((w) => !w)}>
            <FeatherIcon icon={wide ? "smartphone" : "monitor"} size={15} />
          </button>
        )}

        {people.length > 0 && (
          <label className="rn-preview-as">
            Previewing as
            <select value={personId || people[0].recipient_id} onChange={(e) => setPersonId(e.target.value)}>
              {people.map((p) => (
                <option key={p.recipient_id} value={p.recipient_id}>
                  {p.name} · Unit {p.unit_no}
                </option>
              ))}
            </select>
          </label>
        )}

        {onClose && (
          <button type="button" className="rn-ib rn-preview-close" onClick={onClose} title="Close preview">
            <FeatherIcon icon="x" size={16} />
          </button>
        )}
      </div>

      <div className="rn-preview-stage">
        {state.error ? (
          <p className="rn-preview-msg">{state.error}</p>
        ) : !d ? (
          <p className="rn-preview-msg">Building the preview…</p>
        ) : (
          <>
            {tab === "email" && (
              <>
                <p className="rn-preview-subject">
                  <span>Subject</span> {d.email_subject}
                </p>
                <iframe
                  title="Email preview"
                  className={wide ? "rn-preview-frame" : "rn-preview-frame is-phone"}
                  sandbox=""
                  srcDoc={d.email_html}
                />
              </>
            )}

            {tab === "sms" && (
              <div className="rn-preview-sms">
                <p className="rn-preview-bubble">{d.sms_body}</p>
                <p className="rn-preview-msg">
                  {d.sms_body.length} characters · {d.sms_segments} segment{d.sms_segments === 1 ? "" : "s"}
                  {d.sms_segments > 1 ? " · shorten the title to fit one" : ""}
                </p>
              </div>
            )}

            {tab === "page" && (
              <div className="rn-preview-page">
                <p className="rn-preview-eyebrow">{draft.type} notice</p>
                <h3>{draft.title || "Untitled notice"}</h3>
                <p className="rn-preview-meta">
                  {d.sample_recipient?.name} · Unit {d.sample_recipient?.unit_no}
                  {d.sample_recipient?.property_name ? ` · ${d.sample_recipient.property_name}` : ""}
                </p>
                <p className="rn-preview-body">{draft.message}</p>
                <p className="rn-preview-msg">Photos and video appear here at full size when the resident opens the link.</p>
              </div>
            )}
          </>
        )}
        {state.loading && d ? <span className="rn-preview-updating">Updating…</span> : null}
      </div>
    </div>
  );
};

export { NoticePreview };
export default NoticePreview;
